import React from "react";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import { FileText, CheckCircle, AlertTriangle } from "lucide-react";

interface PolicyProps {
  propertyData: PropertyToEdit | null;
}

const Policy: React.FC<PolicyProps> = ({ propertyData }) => {
  const hasRules = propertyData?.additional_rules && propertyData.additional_rules.length > 0;

  return (
    <div className="listingSection__wrap w-full">
      {/* Header Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-50 dark:bg-orange-900/20 rounded-xl">
            <FileText size={24} className="text-[#f5694b] dark:text-orange-400" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Property rules
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Additional guidelines for your stay
            </p>
          </div>
        </div>
      </div>

      <div className="w-full h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-700 to-transparent my-6" />

      {/* Content Section */}
      {hasRules ? (
        <div className="space-y-4">
          {/* Rules List */}
          <div className="grid gap-3">
            {propertyData.additional_rules.map((rule, index) => (
              <div
                key={rule.id}
                className="group flex items-start gap-4 p-4 bg-gradient-to-br from-white to-gray-50 dark:from-neutral-800 dark:to-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-700 hover:border-[#f5694b]/40 dark:hover:border-[#f5694b]/40 hover:shadow-md transition-all duration-200"
              >
                {/* Number Badge */}
                <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-orange-50 dark:bg-orange-900/30 text-[#f5694b] dark:text-orange-400 font-semibold text-sm group-hover:bg-orange-100 dark:group-hover:bg-orange-900/50 transition-colors">
                  {index + 1}
                </div>

                {/* Rule Content */}
                <div className="flex-1 min-w-0 pt-0.5">
                  <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    {rule.name}
                  </p>
                </div>

                {/* Check Icon */}
                <CheckCircle 
                  size={20} 
                  className="flex-shrink-0 text-gray-400 dark:text-gray-600 group-hover:text-[#f5694b] dark:group-hover:text-orange-400 transition-colors mt-0.5" 
                />
              </div>
            ))}
          </div>

          {/* Info Banner */}
          <div className="mt-6 p-5 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-200 dark:border-orange-800">
            <div className="flex gap-3">
              <AlertTriangle size={20} className="text-[#f5694b] dark:text-orange-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-900 dark:text-white font-semibold mb-1.5">
                  Please review carefully
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  By booking this property, you agree to follow all the rules and policies listed above. 
                  Failure to comply may result in booking cancellation.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Empty State */
        <div className="py-12 px-6 bg-gradient-to-br from-gray-50 to-white dark:from-neutral-800 dark:to-neutral-900 rounded-2xl border-2 border-dashed border-gray-300 dark:border-neutral-700">
          <div className="flex flex-col items-center text-center max-w-md mx-auto space-y-3">
            <div className="p-4 bg-gray-100 dark:bg-neutral-700 rounded-full">
              <FileText size={32} className="text-gray-400 dark:text-gray-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                No additional rules
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                This property follows standard house rules. Please refer to the general 
                cancellation policy and house rules sections for more information.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Policy;